import React, { useEffect, useState } from "react";
import { Plus, Trash2, ShieldCheck } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { Shell, Empty } from "@/components/Shell";
import { Select } from "@/pages/Projects";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const TYPES = ["Stage Gate", "Change Request", "Decision", "Risk Acceptance", "Weekly Report"];
const LEVELS = ["Level 1", "Level 2", "Level 3"];
const BLANK = { name: "", email: "", job_title: "", approval_type: "Stage Gate", level: "Level 1", threshold: "", portfolio_id: "" };

export default function AdminApprovers() {
  const [approvers, setApprovers] = useState([]);
  const [portfolios, setPortfolios] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(BLANK);

  const load = () => api.get("/approvers").then(({ data }) => setApprovers(data));

  useEffect(() => {
    load();
    api.get("/portfolios").then(({ data }) => setPortfolios(data));
  }, []);

  const save = async () => {
    if (!form.name.trim()) return toast.error("Approver name is required");
    try {
      await api.post("/approvers", { ...form, email: form.email || null, portfolio_id: form.portfolio_id || null });
      toast.success("Approver added");
      setOpen(false);
      setForm(BLANK);
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`Remove ${a.name} as an approver?`)) return;
    try {
      await api.delete(`/approvers/${a.id}`);
      load();
      toast.success("Approver removed");
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  const pfName = (id) => portfolios.find((p) => p.id === id)?.name || "All portfolios";

  return (
    <Shell
      testId="admin-approvers-page"
      title="Approver Setup"
      subtitle="Who signs off stage gates, change requests, decisions and risk acceptance"
      actions={
        <Button data-testid="new-approver-btn" onClick={() => { setForm(BLANK); setOpen(true); }} className="gap-2" style={{ backgroundColor: "var(--brand-primary)" }}>
          <Plus size={15} /> Add Approver
        </Button>
      }
    >
      {approvers.length === 0 ? (
        <Empty testId="approvers-empty" text="No approvers configured yet. Add the people who sign off governance items." />
      ) : (
        <div className="space-y-6">
          {TYPES.filter((t) => approvers.some((a) => a.approval_type === t)).map((type) => (
            <section key={type} className="panel overflow-hidden" data-testid={`approver-group-${type.toLowerCase().replace(/ /g, "-")}`}>
              <div className="flex items-center gap-2 border-b border-slate-100 px-5 py-3">
                <ShieldCheck size={16} style={{ color: "var(--brand-primary)" }} />
                <h3 className="font-display text-base font-semibold text-slate-800">{type}</h3>
              </div>
              <table className="w-full text-sm">
                <thead className="bg-slate-50 text-left text-[11px] uppercase tracking-wider text-slate-500">
                  <tr>{["Approver", "Job Title", "Level", "Threshold", "Scope", ""].map((h) => <th key={h} className="px-5 py-2.5 font-semibold">{h}</th>)}</tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {approvers.filter((a) => a.approval_type === type).map((a) => (
                    <tr key={a.id} data-testid={`approver-row-${a.id}`}>
                      <td className="px-5 py-3">
                        <div className="font-medium text-slate-800">{a.name}</div>
                        <div className="text-xs text-slate-400">{a.email || "No email"}</div>
                      </td>
                      <td className="px-5 py-3 text-slate-600">{a.job_title || "—"}</td>
                      <td className="px-5 py-3 text-slate-600">{a.level}</td>
                      <td className="px-5 py-3 text-slate-600">{a.threshold || "—"}</td>
                      <td className="px-5 py-3 text-slate-600">{pfName(a.portfolio_id)}</td>
                      <td className="px-5 py-3 text-right">
                        <button data-testid={`delete-approver-${a.id}`} onClick={() => remove(a)} aria-label="Delete approver" title="Delete approver" className="text-slate-400 transition-colors hover:text-red-600">
                          <Trash2 size={15} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </section>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="approver-dialog">
          <DialogHeader>
            <DialogTitle>Add Approver</DialogTitle>
            <DialogDescription>Approvers are matched to governance items by type, level and portfolio scope.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="sm:col-span-2"><label className="field-label">Name *</label><Input data-testid="approver-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div><label className="field-label">Email</label><Input data-testid="approver-email-input" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div><label className="field-label">Job Title</label><Input data-testid="approver-title-input" value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} /></div>
            <div><label className="field-label">Approval Type</label><Select testId="approver-type-select" value={form.approval_type} onChange={(v) => setForm({ ...form, approval_type: v })} options={TYPES} placeholder="Select type" /></div>
            <div><label className="field-label">Approval Level</label><Select testId="approver-level-select" value={form.level} onChange={(v) => setForm({ ...form, level: v })} options={LEVELS} placeholder="Select level" /></div>
            <div><label className="field-label">Threshold / Limit</label><Input data-testid="approver-threshold-input" value={form.threshold} onChange={(e) => setForm({ ...form, threshold: e.target.value })} placeholder="e.g. up to £50k" /></div>
            <div>
              <label className="field-label">Portfolio Scope</label>
              <Select testId="approver-portfolio-select" value={form.portfolio_id} onChange={(v) => setForm({ ...form, portfolio_id: v })} options={portfolios.map((p) => ({ value: p.id, label: p.name }))} placeholder="All portfolios" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" data-testid="approver-cancel-btn" onClick={() => setOpen(false)}>Cancel</Button>
            <Button data-testid="approver-save-btn" onClick={save} style={{ backgroundColor: "var(--brand-primary)" }}>Add Approver</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Shell>
  );
}
